import { Injectable, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import * as AppAction from '../admin-stores/app.action';
import { languageSelector, mapCurrentDataSelector, mapResetCurrentDataSelector, mapTilesSelector } from '../admin-stores/app.selector';

@Injectable({
  providedIn: 'root'
})
export class StoreManagerService {

  private readonly currentLanguage = signal<string>('en-GB');
  private readonly mapTheme = signal<string>('normal');
  private readonly currentMapDetails = signal<{ latLng: string; zoom: number }>({ latLng: '52.520007,13.404954', zoom: 3.75 });
  private readonly destroyRef = inject(DestroyRef);
  private _store = inject(Store);

  /**
   * @description This is called when user selects a language from the language selection modal
   * @param languageId 
   */
  public setUserLanguageToStore(languageId: string) {
    this._store.dispatch(AppAction.setLanguage({ language: languageId }));
  }

  public getUserSelectedLanguageFromStore(): string {
    this._store.select(languageSelector).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((currentLanguage: string) => {
      this.currentLanguage.set(currentLanguage);
    });
    return this.currentLanguage();
  }

  /**
   * @description This is called when user selects a theme for the map - satellite or map
   * @param theme 
   */
  public setMapThemeToStore(theme: string) {
    this._store.dispatch(AppAction.setUserSelectedMapTiles({ mapTiles: theme }));
  }

  /**
  * @description This is called when the application is loaded.
  * This is get the map theme - satellite or normal
  */
  public getMapThemeFromStore() {
    this._store.select(mapTilesSelector).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((mapThemeSelected) => {
      this.mapTheme.set(mapThemeSelected);
    });
    return this.mapTheme();
  }

  /**
   * @description This is called when user changes the map coordinates or zoom level
   * we subscribe to the store to get the updated map coordinates and zoom level
   * so that when user navigates to other pages and comes back, we load the map page where he last left
   * @param mapCurrentData 
   */
  public setCurrentMapDataToStore(mapCurrentData: { latLng: string, zoom: number }) {
    this._store.dispatch(AppAction.mapCurrentData({ mapData: mapCurrentData }));
  }

  public getCurrentMapDataFromStore() {
    this._store.select(mapCurrentDataSelector).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((mapCoordinatesWithZoom) => {
      this.currentMapDetails.set(mapCoordinatesWithZoom);
    });
    return this.currentMapDetails();
  }

  public setMapDataOnLoad(initialMapCurrentData: { latLng: string, zoom: number }) {
    this._store.dispatch(AppAction.mapResetCurrentData({ mapResetData: initialMapCurrentData }));
  }

  public getMapDataOnLoad() {
    this._store.select(mapResetCurrentDataSelector).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((mapCoordinatesWithZoom) => {
      this.currentMapDetails.set(mapCoordinatesWithZoom);
    });
    return this.currentMapDetails();
  }

}
